import React from "react";
import 'regenerator-runtime/runtime';
// Field types
// -----------
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
 * Create an array of ancestor nodes for a given node predicate.
 */
export function findAncestorFields(tree, term) {
  if (tree.field.term === term) return Seq.of(tree.field);
  const ancestors = Seq.from(tree.children).flatMap(child => findAncestorFields(child, term));
  if (ancestors.isEmpty()) return Seq.empty();
  return Seq.of(tree.field).concat(ancestors);
}

/** From TreeUtils */

/**
 * Create a Seq of tree nodes in preorder sequence.
 *
 *              1
 *             / \
 *            /   \
 *           /     \
 *          2       3
 *         / \     /
 *        4   5   6
 *       /       / \
 *      7       8   9
 *
 *     preorder:    1 2 4 7 5 3 6 8 9
 *
 * @param {Object} root
 * @return {Seq}
 */
export function preorderSeq(root) {
  return Seq.from(preorder(root, n => n.children));
}

/**
 * Convert a tree into a new tree-like structure. The tree is traversed bottom-
 * up, and for each node, its mapped children are passed to the mapping
 * function. This allows the mapping function to integrate the mapped children
 * however it needs to.
 *
 * @param {mapFn} mapFn Mapping function to apply to each node.
 * @param {Function} getChildren A function that returns an iterable object over a node's children.
 * @param {any} root The root node of the tree whose structure is being mapped.
 */
export function mapStructure(mapFn, getChildren, root) {
  const mappedChildren = Seq.from(getChildren(root)).map(child => mapStructure(mapFn, getChildren, child)).toArray();
  return mapFn(root, mappedChildren);
}

/**
 * Simple convenience method to identify nodes that are leaves
 * @param {Object} node representing root of subtree (possibly a leaf)
 * @return {Boolean} indicates true if the node is a leaf and false otherwise
 */
export function isLeaf(node, getNodeChildren) {
  return getNodeChildren(node).length === 0;
}

/**
 * Determine if a node is a branch
 * @param node
 * @param getNodeChildren
 */
export function isBranch(node, getNodeChildren) {
  return !isLeaf(node, getNodeChildren);
}

/**
 * Using recursion to return all the leaf nodes for the given node.
 * @param {Object} node representing root of subtree
 * @param {Array} initial list of leaf nodes (optional)
 * @return {Array} updated list of leaf nodes
 */
export function getLeaves(node, getNodeChildren) {
  return Seq.from(preorder(node, getNodeChildren)).filter(node => isLeaf(node, getNodeChildren)).toArray();
}

/**
 * Using recursion to return all the branch nodes for a given node
 * @param {Object} node representing root of subtree
 * @param {Array} initial list of branch nodes (optional)
 * @return {Array} updated list of branch nodes
 */
export function getBranches(node, getNodeChildren) {
  let branches = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  return Seq.from(preorder(node, getNodeChildren)).filter(node => isBranch(node, getNodeChildren)).toArray();
}

/** top-down tree node iterator */
export function* preorder(root, getChildren) {
  yield root;
  const children = getChildren(root);
  const length = children.length;
  for (let i = 0; i < length; i++) {
    yield* preorder(children[i], getChildren);
  }
}

/** From IterableUtils */

// Type definitions

/**
 * Useful operators for Iterables.
 *
 * The module exports the class `Seq` which creates a wrapper that exposes
 * a fluent interface for traversing and manipulating the underlying iterable
 * object using operators.
 *
 * The module also exports each operator as a function for use in isolation.
 *
 * All methods and functions return a new Iterable object. Iteration is lazy
 * and will only execute when a value is requested (toArray, reduce, etc).
 * The iteration will terminate as early as possible.
 *
 * For example, the following `Seq` code will only iterate 3 times:
 *
 *    let array = [];
 *    for (let i = 1; i <= 1000; i++) {
 *      array.push(i);
 *    }
 *
 *    // 3 iterations
 *    Seq.from(array)
 *      .map(n => n * n)
 *      .filter(n => n % 2 === 1)
 *      .takeUntil(n => n > 30)
 *      .toArray() // [ 1, 9, 25 ]
 *
 */

/**
 * Wraps `iterable` in an object with collection operations.
 */
export class Seq {
  static EMPTY = new Seq([]);

  // constructors

  static empty() {
    return Seq.EMPTY;
  }
  static of() {
    for (var _len = arguments.length, values = new Array(_len), _key = 0; _key < _len; _key++) {
      values[_key] = arguments[_key];
    }
    return values.length === 0 ? Seq.empty() : new Seq(values);
  }
  static from(iterable) {
    return new Seq(iterable);
  }
  static concat() {
    return Seq.from(concat(...arguments));
  }
  _cache = [];
  constructor(_iterable) {
    this._iterable = _iterable;
  }
  *[Symbol.iterator]() {
    // Since this._iterator can be a generator object, we cache the iteration
    // so it can be replayed. Generator objects are stateful, so this is
    // necessary.
    yield* this._cache;
    if (this._iterator == null) {
      this._iterator = this._iterable[Symbol.iterator]();
    }
    while (true) {
      const {
        done,
        value
      } = this._iterator.next();
      if (done) {
        break;
      } else {
        this._cache.push(value);
        yield value;
      }
    }
  }
  concat() {
    for (var _len2 = arguments.length, iterables = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      iterables[_key2] = arguments[_key2];
    }
    return new Seq(concat(this, ...iterables));
  }
  map(fn) {
    return new Seq(map(fn, this));
  }
  flatMap(fn) {
    return new Seq(flatMap(fn, this));
  }
  uniq() {
    return new Seq(uniq(this));
  }
  filter(fn) {
    return new Seq(filter(fn, this));
  }
  some(fn) {
    return some(fn, this);
  }
  reduce(fn, value) {
    return reduce(fn, value, this);
  }
  toArray() {
    return this.reduce((arr, item) => (arr.push(item), arr), []);
  }
  isEmpty() {
    return !this.some(() => true);
  }
}

// XXX The for..of loop construct is not being used because babel adds a
// try-catch to the loop body, which deoptimizes the code path. See
// https://github.com/google/traceur-compiler/issues/1773.

export function* concat() {
  for (let i = 0; i < arguments.length; i++) {
    yield* i < 0 || arguments.length <= i ? undefined : arguments[i];
  }
}
export function* map(fn, iterable) {
  for (let iter = iterable[Symbol.iterator]();;) {
    const {
      done,
      value
    } = iter.next();
    if (done) break;
    yield fn(value);
  }
}
export function* flatMap(fn, iterable) {
  for (let iter = iterable[Symbol.iterator]();;) {
    const {
      done,
      value
    } = iter.next();
    if (done) break;
    yield* fn(value);
  }
}
export function* uniq(iterable) {
  const values = new Set();
  for (let iter = iterable[Symbol.iterator]();;) {
    const {
      done,
      value
    } = iter.next();
    if (done) break;
    if (values.has(value) === false) {
      values.add(value);
      yield value;
    }
  }
}
export function* filter(fn, iterable) {
  for (let iter = iterable[Symbol.iterator]();;) {
    const {
      done,
      value
    } = iter.next();
    if (done) break;
    if (fn(value)) yield value;
  }
}
export function some(test, iterable) {
  for (let iter = iterable[Symbol.iterator]();;) {
    const {
      done,
      value
    } = iter.next();
    if (done) break;
    if (test(value) === true) return true;
  }
  return false;
}

/**
 * Reduce collection to a single value.
 */
export function reduce(fn, seedValue, iterable) {
  let result = seedValue;
  for (let iter = iterable[Symbol.iterator]();;) {
    const {
      done,
      value
    } = iter.next();
    if (done) break;
    result = fn(result, value);
  }
  return result;
}

/** From ComponentUtils */

/** Create a React Element using preformatted HTML */

export function safeHtml() {
  let str = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  let props = arguments.length > 1 ? arguments[1] : undefined;
  let Component = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'span';
  if (str.indexOf('<') === -1) {
    return _jsx(Component, {
      ...props,
      children: str
    });
  }
  // Use innerHTML to auto close tags
  const container = document.createElement('div');
  container.innerHTML = str;
  return _jsx(Component, {
    ...props,
    dangerouslySetInnerHTML: {
      __html: container.innerHTML
    }
  });
}

/**
 * Makes a copy of current, adds value if not present, removes if present, and
 * returns the copy.
 * @param {Array<T>} array array to modify
 * @param {<T>} value to check against
 * @return {Array<T>} modified copy of original array
 */
export function addOrRemove(array, value) {
  return array.indexOf(value) == -1 ?
  // not currently present; add
  array.concat(value) :
  // already there; remove
  array.filter(elem => elem != value);
}

/** From SearchUtils */

/**
 * Split search query string on whitespace, unless wrapped in quotes
 * @param {string} searchQueryString A string representing the search query
 * @returns {Array<String>} A set of query terms parsed from searchQueryString
 */
export function parseSearchQueryString(searchQueryString) {
  const match = searchQueryString.match(/[^\s"]+|"[^"]*"/g);
  if (match == null) return [];
  return match.map(function (queryTerm) {
    if (queryTerm.startsWith('"')) {
      // remove wrapping quotes and asterisks from phrases
      return queryTerm.replace(/(^")|("$)|\*/g, '');
    } else {
      // remove stray quotes
      return queryTerm.replace(/(^")|("$)/g, '');
    }
  });
}